import { DuplicateSvg, ErrorSvg, WarningSvg } from "@/components/Icon/Icon";
import { StyledTooltip } from "@/components/StyledTooltip/StyledTooltip";
import { OpenErrorCodeDocumentation } from "@/constants/issues";
import {
  selectCaseDuplicates,
  selectCaseErrors,
  selectCaseWarnings,
  selectScanDuplicates,
  selectScanErrors,
  selectScanWarnings
} from "@/store/slices/casesSlice";
import { RootState } from "@/store/store";
import { theme } from "@/themes/theme";
import { ErrorCode } from "@/typed/Issue";
import { Box, Link, Typography } from "@mui/material";
import { ReactElement, ReactNode } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

// Custom content for the tooltip
const TooltipContent = ({ title, message }: { title: ReactNode; message: ReactNode }) => (
  <Box>
    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
      {title}
    </Typography>
    <Typography variant="body2">{message}</Typography>
  </Box>
);

interface IssueIndicatorProps {
  uploadId: number;
  scanId?: string;
}

const IssueTooltip = ({ errorCode, children }: { errorCode: ErrorCode; children: ReactElement }) => {
  const { t } = useTranslation("cases");
  return (
    <StyledTooltip
      title={
        <TooltipContent
          title={t(`error.${errorCode}.title`)}
          message={
            <Trans
              t={t}
              components={{
                a: (
                  <Link
                    color={theme.palette.primary.contrastText}
                    component={"button"}
                    onClick={() => {
                      OpenErrorCodeDocumentation(errorCode);
                    }}
                  />
                )
              }}
              i18nKey={`error.${errorCode}.message`}
            />
          }
        />
      }
      placement="bottom" // placement can be adjusted; the arrow will auto-flip if needed
    >
      {children}
    </StyledTooltip>
  );
};

export const IssueIndicator: React.FC<IssueIndicatorProps> = ({ uploadId, scanId: scanIndex }: IssueIndicatorProps) => {
  const errors =
    useSelector((state: RootState) => {
      if (scanIndex == null) {
        return selectCaseErrors(state, uploadId);
      } else {
        return selectScanErrors({ state, uploadId, scanId: scanIndex });
      }
    }) ?? [];
  const warnings =
    useSelector((state: RootState) => {
      if (scanIndex == null) {
        return selectCaseWarnings(state, uploadId);
      } else {
        return selectScanWarnings({ state, uploadId, scanId: scanIndex });
      }
    }) ?? [];

  return (
    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minWidth: "25px" }}>
      {errors?.length > 0 ? (
        <IssueTooltip errorCode={errors[0].code}>
          <ErrorSvg />
        </IssueTooltip>
      ) : warnings?.length > 0 ? (
        <IssueTooltip errorCode={warnings[0].code}>
          <WarningSvg color={theme.palette.warning.main} />
        </IssueTooltip>
      ) : (
        <Box></Box>
      )}
    </Box>
  );
};

export const DuplicateIndicator: React.FC<IssueIndicatorProps> = ({ uploadId, scanId }: IssueIndicatorProps) => {
  const { t } = useTranslation("cases");

  const duplicates =
    useSelector((state: RootState) => {
      if (scanId == null) {
        return selectCaseDuplicates(state, uploadId);
      } else {
        return selectScanDuplicates({ state, uploadId, scanId: scanId });
      }
    }) ?? [];

  return (
    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minWidth: "25px" }}>
      {duplicates?.length > 0 && (
        <StyledTooltip
          title={
            <TooltipContent title={t(`duplicate.${duplicates[0].code}.title`)} message={t(`duplicate.${duplicates[0].code}.message`)} />
          }
          placement="bottom" // placement can be adjusted; the arrow will auto-flip if needed
        >
          <DuplicateSvg color={theme.palette.secondary.main} />
        </StyledTooltip>
      )}
    </Box>
  );
};
